<template>
  <div class="my-devices">
    <!-- 数据展示区域 -->
    <div class="data-section">
      <div class="section-header">
        <div>
          <span style="font-size: 14px; color: #333">账户管理</span>
        </div>
        <div class="toolbar">
          <el-button v-if="userRole === 0" type="primary" @click="openAddDialog">新增</el-button>

          <!-- 刷新按钮 -->
          <el-button type="text" @click="refreshData">
            <el-icon><Refresh /></el-icon>
          </el-button>

          <!-- 密度设置 -->
          <el-popover
            placement="bottom"
            width="160"
            v-model:visible="densityDialogVisible"
          >
            <div class="popover-content">
              <el-button
                class="density-button"
                :type="rowHeight === '80px' ? 'primary' : ''"
                @click="setRowHeight('80px')"
              >
                默认
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '60px' ? 'primary' : ''"
                @click="setRowHeight('60px')"
              >
                中等
              </el-button>
              <el-button
                class="density-button"
                :type="rowHeight === '30px' ? 'primary' : ''"
                @click="setRowHeight('30px')"
              >
                紧凑
              </el-button>
            </div>
            <template #reference>
              <el-button type="text" class="-button-spacustomcing">
                <el-icon><Grid /></el-icon>
              </el-button>
            </template>
          </el-popover>

          <!-- 列显示设置 -->
          <el-popover
            placement="bottom"
            width="100"
            v-model:visible="columnsDialogVisible"
          >
            <div class="popover-content">
              <el-checkbox-group v-model="tempSelectedColumns">
                <el-checkbox
                  v-for="column in columns"
                  :key="column.prop"
                  :label="column.prop"
                >
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
              <div class="popover-footer">
                <el-button @click="resetColumns">重置</el-button>
                <el-button type="primary" @click="confirmColumns">确定</el-button>
              </div>
            </div>
            <template #reference>
              <el-button type="text">
                <el-icon><Setting /></el-icon>
              </el-button>
            </template>
          </el-popover>
        </div>
      </div>

      <!-- 表格区域 -->
      <el-table
        :data="tableData"
        style="width: 98.5%; margin-left: 10px; margin-right: 20px; margin-top: 10px"
        :row-style="getRowStyle"
        :header-cell-style="{
          background: '#f5f7fa',
          color: '#909399',
          textAlign: 'center',
        }"
        :row-class-name="tableRowClassName"
      >
        <el-table-column
          v-for="column in visibleColumns"
          :key="column.prop"
          :prop="column.prop"
          :label="column.label"
          :width="column.width"
          :align="column.align || 'center'"
        >
          <template #default="scope">
            <template v-if="column.prop === 'operation'">
              <div class="operation-buttons">
                <el-button
                  type="text"
                  style="color: red"
                  @click="deleteAccount(scope.row.id)"
                >
                  删除
                </el-button>
                <el-button
                  type="text"
                  style="color: blue"
                  @click="editAccount(scope.row)"
                >
                  编辑
                </el-button>
              </div>
            </template>
            <template v-else>
              {{ scope.row[column.prop] }}
            </template>
          </template>
        </el-table-column>

        <template #empty>
          <div class="empty-state">
            <el-icon class="empty-icon"><Box /></el-icon>
            <p class="empty-text">暂无数据</p>
          </div>
        </template>
      </el-table>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      class="custom-dialog"
      @close="closeDialog"
    >
      <div class="dialog-content">
        <el-form :model="formData" label-width="100px" :rules="rules" ref="formRef">
          <el-form-item label="账号" prop="username">
            <el-input v-model="formData.username" placeholder="请输入账号" />
          </el-form-item>
          <el-form-item label="姓名" prop="name">
            <el-input v-model="formData.name" placeholder="请输入姓名" />
          </el-form-item>
          <el-form-item label="密码" prop="password" v-if="dialogTitle === '新增账户'">
            <el-input
              v-model="formData.password"
              type="password"
              placeholder="请输入密码"
            />
          </el-form-item>
          <el-form-item label="角色" prop="role">
            <el-select v-model="formData.role" placeholder="请选择角色">
              <el-option label="管理员" :value="0" />
              <el-option label="员工" :value="1" />
              <el-option label="维修工程师" :value="2" />
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitForm">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { Box, Refresh, Grid, Setting } from "@element-plus/icons-vue";
import {
  getAccountList,
  createAccount,
  updateAccount,
  deleteAccount as deleteAccountApi,
} from "@/api/webadmin";

export default {
  name: "AccountManagePage",
  components: {
    Box,
    Refresh,
    Grid,
    Setting,
  },
  setup() {
    const tableData = ref([]);
    const dialogVisible = ref(false);
    const dialogTitle = ref("新增账户");
    const formRef = ref(null);
    const formData = ref({
      username: "",
      password: "",
      name: "",
      role: 1,
    });

    // 表格配置
    const columns = ref([
      { prop: "index", label: "序号", visible: true },
      { prop: "username", label: "账号", visible: true },
      { prop: "name", label: "姓名", visible: true },
      { prop: "role", label: "角色", visible: true },
      { prop: "createTime", label: "创建时间", visible: true },
      { prop: "lastLoginTime", label: "最后登录时间", visible: true },
      { prop: "operation", label: "操作", visible: true },
    ]);

    const visibleColumns = computed(() =>
      columns.value.filter((col) => selectedColumns.value.includes(col.prop))
    );

    const selectedColumns = ref(columns.value.map((col) => col.prop));
    const tempSelectedColumns = ref([]);
    const rowHeight = ref("80px");
    const densityDialogVisible = ref(false);
    const columnsDialogVisible = ref(false);
    const userRole = ref(0);

    const rules = {
      username: [
        { required: true, message: "请输入账号", trigger: "blur" },
        { min: 3, max: 20, message: "长度在 3 到 20 个字符", trigger: "blur" },
      ],
      name: [
        { required: true, message: "请输入姓名", trigger: "blur" },
        { min: 2, max: 20, message: "长度在 2 到 20 个字符", trigger: "blur" },
      ],
      password: [
        { required: true, message: "请输入密码", trigger: "blur" },
        { min: 6, max: 20, message: "长度在 6 到 20 个字符", trigger: "blur" },
      ],
      role: [{ required: true, message: "请选择角色", trigger: "change" }],
    };

    // 加载账号数据
    const loadData = async () => {
      try {
        const response = await getAccountList();
        tableData.value = response.map((item, index) => ({
          index: index + 1,
          id: item.id,
          username: item.username,
          name: item.name || '-',
          role: item.role === 0 ? "管理员" : item.role === 1 ? "员工" : "维修工程师",
          createTime: item.createTime,
          lastLoginTime: item.lastLoginTime || "-",
          status: item.status,
        }));
      } catch (error) {
        console.error("获取账号列表失败:", error);
        ElMessage.error("获取账号列表失败");
      }
    };

    // 表格样式
    const getRowStyle = () => {
      return { height: rowHeight.value };
    };

    const tableRowClassName = ({ rowIndex }) =>
      rowIndex % 2 === 0 ? "even-row" : "odd-row";

    // 打开新增弹窗
    const openAddDialog = () => {
      dialogTitle.value = "新增账户";
      formData.value = {
        username: "",
        name: "",
        password: "",
        role: "",
      };
      dialogVisible.value = true;
    };

    // 打开编辑弹窗
    const editAccount = (row) => {
      dialogTitle.value = "编辑账户";
      const roleText = row.role === "管理员" ? 0 : row.role === "员工" ? 1 : 2;
      dialogVisible.value = true;
      formData.value = {
        id: row.id,
        username: row.username,
        name: row.name,
        role: roleText,
      };
    };

    // 删除账户
    const deleteAccount = async (id) => {
      try {
        await ElMessageBox.confirm("确认删除该账户？", "提示", {
          type: "warning",
        });
        // 调用删除账号API
        await deleteAccountApi(id);
        // 重新加载数据
        loadData();
        ElMessage.success("删除成功");
      } catch (error) {
        if (error === "cancel") {
          // 用户取消删除
          return;
        }
        console.error("删除账号失败:", error);
        ElMessage.error(error.response?.data?.message || "删除账号失败");
      }
    };

    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();

        if (!formData.value.id) {
          // 创建新账号
          await createAccount({
            username: formData.value.username,
            password: formData.value.password,
            name: formData.value.name,
            role: formData.value.role,
          });
        } else {
          // 更新账号
          await updateAccount({
            id: formData.value.id,
            username: formData.value.username,
            password: formData.value.password || undefined,
            name: formData.value.name,
            role: formData.value.role,
          });
        }

        // 重新加载数据
        loadData();
        ElMessage.success("保存成功");
        dialogVisible.value = false;
      } catch (error) {
        console.error("保存账号失败:", error);
        ElMessage.error(error.response?.data?.message || "保存账号失败");
      }
    };

    // 关闭弹窗
    const closeDialog = () => {
      formRef.value?.resetFields();
      dialogVisible.value = false;
    };

    // 刷新数据
    const refreshData = () => {
      loadData();
      ElMessage.success("数据已刷新");
    };

    // 设置表格行高
    const setRowHeight = (height) => {
      rowHeight.value = height;
      densityDialogVisible.value = false;
    };

    // 重置列显示
    const resetColumns = () => {
      // 根据用户角色决定是否显示操作列
      if (userRole.value === 1 || userRole.value === 2) {
        // 员工和维修工程师不显示操作列
        tempSelectedColumns.value = columns.value
          .filter(col => col.prop !== 'operation')
          .map(col => col.prop);
      } else {
        // 管理员显示所有列
        tempSelectedColumns.value = columns.value.map(col => col.prop);
      }
    };

    // 确认列显示设置
    const confirmColumns = () => {
      if (tempSelectedColumns.value.length === 0) {
        ElMessage.warning("至少选择一列");
        return;
      }
      
      // 非管理员角色强制移除操作列
      if (userRole.value === 1 || userRole.value === 2) {
        const index = tempSelectedColumns.value.indexOf('operation');
        if (index > -1) {
          tempSelectedColumns.value.splice(index, 1);
        }
      }
      
      selectedColumns.value = [...tempSelectedColumns.value];
      columnsDialogVisible.value = false;
    };

    onMounted(() => {
      loadData();
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const user = JSON.parse(userInfo);
        userRole.value = user.role; // 0: 管理员, 1: 员工, 2: 维修工程师
        
        // 非管理员角色隐藏操作列
        if (userRole.value === 1 || userRole.value === 2) {
          const index = selectedColumns.value.indexOf('operation');
          if (index > -1) {
            selectedColumns.value.splice(index, 1);
          }
        }
      }
      tempSelectedColumns.value = [...selectedColumns.value];
    });

    return {
      tableData,
      dialogVisible,
      dialogTitle,
      formRef,
      formData,
      rules,
      columns,
      visibleColumns,
      selectedColumns,
      tempSelectedColumns,
      rowHeight,
      densityDialogVisible,
      columnsDialogVisible,
      getRowStyle,
      tableRowClassName,
      openAddDialog,
      editAccount,
      deleteAccount,
      submitForm,
      closeDialog,
      refreshData,
      setRowHeight,
      resetColumns,
      confirmColumns,
      userRole
    };
  },
};
</script>

<style scoped>
.my-devices {
  background-color: #f5f7fa;
  min-height: 100vh;
}

.data-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
}

.toolbar {
  display: flex;
  gap: 8px;
}

.operation-buttons {
  gap: 8px;
}

.even-row {
  background-color: #fafafa;
}

.odd-row {
  background-color: #ffffff;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #909399;
  margin-bottom: 16px;
}

.empty-text {
  color: #909399;
  margin: 0;
  font-size: 14px;
}

.popover-content {
  display: flex;
  flex-direction: column;
}

.popover-footer {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 10px;
}

.density-button {
  display: flex;
  justify-content: center;
  margin: 10px;
}
</style>
